import React, { useState, useEffect } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";

const LanguageSelectModal = ({ show, onHide, onSelectLanguage, selectedLanguageId }) => {
  const [languages, setLanguages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch languages from API
  useEffect(() => {
    const fetchLanguages = async () => {
      if (!show) return; // Only fetch when modal is shown
      
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetch('https://api.nkenne.com/api/Language/GetAllLearningLanguage');
        
        if (!response.ok) {
          throw new Error(`Failed to fetch languages: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Transform API data to include "All languages" option
        const transformedLanguages = [
          { 
            id: "all", 
            name: "All languages", 
            imagePath: null,
            description: "Show all languages"
          },
          ...data.map(lang => ({
            id: lang.id.toString(), // Ensure ID is string for comparison
            name: lang.name,
            imagePath: lang.imagePath,
            description: lang.description || ""
          }))
        ];
        
        setLanguages(transformedLanguages);
      } catch (err) {
        console.error('Error fetching languages:', err);
        setError('Failed to load languages');
        
        // Fallback to empty array with "All languages" option
        setLanguages([{ 
          id: "all", 
          name: "All languages", 
          imagePath: null,
          description: "Show all languages"
        }]);
      } finally {
        setIsLoading(false);
      }
    };

    if (show) {
      fetchLanguages();
    }
  }, [show]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setLanguages([]);
      setError(null);
    }
  }, [show]);

  const handleLanguageSelect = (language) => {
    onSelectLanguage({
      id: language.id,
      name: language.name
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Select Language</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading languages...</span>
            </Spinner>
            <div className="mt-2">Loading languages...</div>
          </div>
        ) : error ? (
          <div className="text-center py-4 text-danger">
            <div>{error}</div>
            <small className="text-muted">Using limited language list</small>
          </div>
        ) : (
          <div className="list-group">
            {languages.map((lang) => (
              <button
                key={lang.id}
                className={`list-group-item list-group-item-action d-flex align-items-start p-3 ${
                  selectedLanguageId === lang.id ? "active" : ""
                }`}
                onClick={() => handleLanguageSelect(lang)}
              >
                <div className="d-flex align-items-center w-100">
                  {lang.imagePath && (
                    <div className="me-3">
                      <img 
                        src={`${process.env.REACT_APP_FLAG_IMAGE_PATH}${lang.imagePath}`}
                        alt={lang.name}
                        style={{
                          width: '30px',
                          height: '22px',
                          objectFit: 'cover',
                          borderRadius: '3px'
                        }}
                        onError={(e) => {
                          // Hide broken images
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="text-start flex-grow-1">
                    <div className="fw-bold">{lang.name}</div>
                    {lang.description && (
                      <small className={`d-block ${selectedLanguageId === lang.id ? 'text-white-50' : 'text-muted'}`}>
                        {lang.description}
                      </small>
                    )}
                  </div>
                  {selectedLanguageId === lang.id && (
                    <div className="ms-2">
                      <i className="fa fa-check"></i>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LanguageSelectModal;